import { defineComponent as _defineComponent } from 'vue'
import Loader from '../loader.vue';
import { getUserId } from '@/util/auth';
import { getUserAvailability } from '@/util/functions';
import { computed, onMounted, ref } from 'vue';
import { Userspaces, Weekday } from '@busy-human/opt-library';
import Calendar from './calendar.vue';
import TimeScroller from './time-scroller.vue';
import { useUserspace } from '@/store/userspace';

type EventTimes = Awaited<ReturnType<typeof getUserAvailability>>['data'][number]
type UserSetAvailability = Userspaces.Model['timesAvailable'];


export default /*#__PURE__*/_defineComponent({
  __name: 'temp-container',
  setup(__props, { expose }) {
  expose();


const loading = ref(false);
const selectedDate = ref(new Date());

const currentMonth = ref(new Date().getMonth());
const currentYear = ref(new Date().getFullYear());
const currentYearMonth = computed<`${number}-${number}`>(() => `${currentMonth.value}-${currentYear.value}`);
const timeCache = ref<Record<`${number}-${number}`, Date[][]>>({});

const adjustMonth = (delta = 1) => {
    const year = currentYear.value;
    const month = currentMonth.value;

    currentMonth.value = (month + delta) % 12;
    currentYear.value = year + Math.floor((month + delta) / 12);
}

const slotsForMonth = computed<Date[][]>(() => {
    const dash = currentYearMonth.value.indexOf('-')
    const month = Number(currentYearMonth.value.substring(0, dash));
    const year = Number(currentYearMonth.value.substring(dash + 1));

    const daysInMonth = new Date(year, month, 0).getDate();
    
    const tmp: Date[][] | undefined = timeCache.value[currentYearMonth.value];
    
    const currentId = useUserspace().uid;
    if(!currentId) return Array(daysInMonth).fill([]);

    if(!tmp) {
        loading.value = true;
        computeAvailableTimesForMonth(year, month, 30, currentId).then(val => {
            timeCache.value[`${month}-${year}`] = val;
            loading.value = false;
        })
        return Array(daysInMonth).fill([]);
    } else { 
        return tmp;
    }
})

const weekdaymap: Weekday[] = [Weekday.SUN, Weekday.MON, Weekday.TUE, Weekday.WED, Weekday.THU, Weekday.FRI, Weekday.SAT];

/** Does user allow meetings on a specific date */
function isAvailableOnDay(date: Date, avail: UserSetAvailability): boolean {
    if(!avail) return false;

    const dayOfWeek = weekdaymap[date.getDay()];
    
    for(const tmp of avail) {
        if(tmp.days.includes(dayOfWeek)) return true;
    };
    return false;
}

/** Get times a user is available on a specific date, via their start and end hours */
function getStartEndHour(date: Date, avail: UserSetAvailability): {startHour: number, endHour: number} {
    if(!avail) return { startHour: 1, endHour: 24 };

    const dayOfWeek = weekdaymap[date.getDay()];

    for(const tmp of avail) {
        if(tmp.days.includes(dayOfWeek)) return { startHour: tmp.startTime, endHour: tmp.endTime }
    }

    return { startHour: 1, endHour: 24 };
}

/** Computes all available time slots for a given month */
async function computeAvailableTimesForMonth(year: number, month: number, length: number, userId: string) {
    const [availability, userspace] = await Promise.all([
        getUserAvailability({
            uid: userId, 
            month: selectedDate.value.getMonth(), 
            year: selectedDate.value.getFullYear()
        }),
        Userspaces.Collection.fetchData(userId)
    ]);

    const events = availability.data;
    const avail = userspace.timesAvailable;
    
    const daysInMonth = new Date(year, month, 0).getDate();

    const dateAvailability: Date[][] = [];

    for(let i = 0; i < daysInMonth; i++) {
        const day = new Date(year, month, i + 1);
        if(!isAvailableOnDay(day, avail)) dateAvailability.push([]);
        else {
            const {startHour, endHour} = getStartEndHour(day, avail);
            const tmp = getAvailableTimes(day, length, startHour, endHour, events);
            dateAvailability.push(tmp);
        }
    }

    return dateAvailability;
}

/** Checks if there's a collision between two events */
function checkTimeCollision(a: {start: Date, end: Date}, b: {start: Date, end: Date}) {
    return a.end.getTime() > b.start.getTime() && b.end.getTime() > a.start.getTime();
}

/** Computes available times for a specific day, given a start and end hour, and events */
function getAvailableTimes(date: Date, length: number, startHour: number, endHour: number, events: EventTimes[]): Date[] {
    const start = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 
        startHour, 0);
    const end = new Date(date.getFullYear(), date.getMonth(), date.getDate(),
        endHour, 0);

    const incrementMinutes = 30;

    const openings: Date[] = [];

    let compareDate = start;
    let i = 0; 
    while (compareDate.getTime() < end.getTime()) {

        let collided = false;
        for(const event of events) {
            const tmp = new Date(compareDate);
            tmp.setMinutes(tmp.getMinutes() + length);
            const a = {
                start: compareDate,
                end: tmp
            }
            const b = {
                start: new Date(event.startTime),
                end: new Date(event.endTime)
            }
            if(checkTimeCollision(a, b)) {
                collided = true;
                break;
            }
        }
        if(!collided)
            openings.push(compareDate);

        i += incrementMinutes;
        compareDate = new Date(date.getFullYear(), date.getMonth(), date.getDate(),
            startHour, i);
    }

    return openings;
}

const __returned__ = { loading, selectedDate, currentMonth, currentYear, currentYearMonth, timeCache, adjustMonth, slotsForMonth, weekdaymap, isAvailableOnDay, getStartEndHour, computeAvailableTimesForMonth, checkTimeCollision, getAvailableTimes, Loader, getUserId, getUserAvailability, computed, onMounted, ref, Userspaces, Weekday, Calendar, TimeScroller, useUserspace }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})